import React, { useState } from "react";
import { Container, Row, Col, Card, Modal, Button } from "react-bootstrap";
import "./About.css";

interface TeamMember {
  id: number;
  name: string;
  position: string;
  image: string;
  bio: string;
  expertise: string[];
  linkedinUrl: string;
}

const About: React.FC = () => {
  const [showModal, setShowModal] = useState<boolean>(false);
  const [selectedTeamMember, setSelectedTeamMember] = useState<TeamMember | null>(null);

  const teamMembers: TeamMember[] = [
    {
      id: 1,
      name: "Alex Harwig, PhD",
      position: "CEO",
      image: `/images/teams/ceo.jpg`,
      bio: "Alex Harwig is a seasoned scientific leader and innovator who has been at the forefront of drug discovery and technology development since 1998. Over the years, he has guided numerous R&D programs across diagnostics, imaging, and therapeutics, navigating science’s remarkable shift from manual experimentation to high-throughput, data-driven innovation. Known for his hands-on approach and strategic mindset, Alex combines deep scientific insight with a strong commitment to intellectual property protection. His longstanding interest in patents and IP strategy stems directly from his work at the cutting edge of innovation—making him a unique bridge between scientific discovery and the systems that safeguard it.",
      expertise: ["Computational Chemistry", "Drug Discovery", "Pharmaceutical Research", "IP Strategy"],
      linkedinUrl: "https://www.linkedin.com/in/alexharwig/"
    },
    {
      id: 2,
      name: "Makda Gebre, MSc, PhD",
      position: "CBO",
      image: `/images/teams/cbo.jpg`,
      bio: "Makda leverages over a decade of experience in drug discovery, spanning the development of antiviral drugs, RNA therapeutics, pandemic-scale vaccines, and venture-backed startups. A Harvard Medical School-trained virologist and immunologist, she identified the potential of RNA vaccines well before COVID-19, which led to her active involvement in the global pandemic response efforts with companies such as Jansen and CureVac. Inspired by the global impact and continued utility of novel mRNA therapeutics, Makda pivoted towards venture creation and early start ups in the RNA space. Throughout her career, especially in hyper-competitive and saturated therapeutic fields, she repeatedly came across a critical and yet initially overlooked component that determined success in drug development – IP due diligence. At Fyled, her and the team hope to bridge the gap between today’s complex scientific breakthroughs and IP protection.",
      expertise: ["Business Development", "Biotech Commercialization", "Strategic Partnerships", "Market Strategy"],
      linkedinUrl: "https://www.linkedin.com/in/makda-gebre/"
    },
    {
      id: 3,
      name: "Surya SG BE, M.Tech",
      position: "AI Lead",
      image: `/images/teams/ai-lead.jpg`,
      bio: "With close to a decade of experience, Surya specializes in architecting and scaling production grade AI systems. with a deep focus on natural language processing, generative AI, AIOps and AI infrastructure, he drives innovation from research to real-world impact. At Fyled, he leads AI initiatives and end-to-end product development, driving innovation across our intelligent IP management platform tailored towards the life sciences. He oversees the work across the full AI lifecycle, from research and model development to deployment and performance optimization, ensuring scalable, production grade systems that align with our customers’ business goals.",
      expertise: ["Machine Learning", "Natural Language Processing", "Computer Vision", "AI Architecture"],
      linkedinUrl: "https://www.linkedin.com/in/ayrusurya/"
    },
    {
      id: 4,
      name: "Celeste Alvarez, PhD",
      position: "IP Lead",
      image: `/images/teams/ip-lead.jpg`,
      bio: "As a patent professional, Celeste leads the Fyled team on developing and delivering AI-driven IP solutions for biotech and pharma innovators. A scientist at heart, she earned her my PhD in Pharmaceutical Sciences from UW-Madison, specializing in drug delivery. Her interest in infectious disease originally drew her to science, and her belief in the power of AI to democratize high-quality patent services brought her to Fyled. She is excited to help businesses launch, raise funding, and thrive based on their patent-worthy innovations – opportunities, she believes, shouldn't be limited to companies with the biggest budgets.",
      expertise: ["Patent Law", "IP Strategy", "Life Sciences IP", "Patent Prosecution"],
      linkedinUrl: "https://www.linkedin.com/in/celeste-alvarez-pharmsciphd/"
    }
  ];

  const handleTeamMemberClick = (member: TeamMember): void => {
    setSelectedTeamMember(member);
    setShowModal(true);
  };

  const handleCloseModal = (): void => {
    setShowModal(false);
    setSelectedTeamMember(null);
  };

  return (
    <Container className="about-page py-5">
      <Row className="mb-5">
        <Col lg={8} className="mx-auto text-center">
          <h1 className="display-5 fw-bold mb-4">About Fyled AI</h1>
          <p className="lead">
            We empower scientific innovators and IP professionals with the tools
            they need to navigate the complex landscape of IP with unprecedented
            efficiency. By combining cutting-edge artificial intelligence with
            deep biotech expertise, we transform fragmented IP workflows into
            seamless, end-to-end solutions.
          </p>
        </Col>
      </Row>

      <Row className="mb-5 align-items-center">
        <Col lg={8} className="mx-auto text-center">
          <h2>Our Mission</h2>
          <p>
            As scientist founders with decades of experience in drug discovery,
            we have navigated the complex paths of IP in the life sciences. As
            science gets more and more complicated, especially with the
            infiltration of AI in drug discovery, we see the critical need to
            equip today's innovators and IP agents the tools that allow them to
            protect today's therapeutics better, faster and at a more affordable
            cost.
          </p>
        </Col>
      </Row>

      <Row>
        <Col md={12}>
          <h2 className="text-center mb-4">Our Team</h2>
          <p className="text-center mb-5">
            Fyled is led by a strong team of scientists, software engineers and
            IP agents to expertly bridge the fields of drug discovery, AI and
            IP.
          </p>

          <Row>
            {teamMembers.map((member) => (
              <Col lg={3} md={6} className="mb-4" key={member.id}>
                <Card 
                  className="team-card h-100 shadow-sm"
                  onClick={() => handleTeamMemberClick(member)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="team-image-container">
                    <img
                      src={member.image}
                      alt={member.name}
                      className="team-image"
                    />
                  </div>
                  <Card.Body className="text-center p-4">
                    <h4 className="h5">{member.name}</h4>
                    <p className="text-muted mb-2">{member.position}</p>
                    <a 
                      href={member.linkedinUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="linkedin-link"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <img 
                        src="https://cdn.jsdelivr.net/npm/simple-icons@v9/icons/linkedin.svg" 
                        alt="LinkedIn"
                        className="linkedin-icon"
                      />
                    </a>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Col>
      </Row>

      {/* Team Member Details Modal */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>{selectedTeamMember?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedTeamMember && (
            <Row>
              <Col md={4} className="text-center mb-3">
                <div className="modal-team-image-container">
                  <img
                    src={selectedTeamMember.image}
                    alt={selectedTeamMember.name}
                    className="modal-team-image rounded-circle"
                  />
                </div>
                <h5 className="mt-3">{selectedTeamMember.name}</h5>
                <p className="text-muted mb-2">{selectedTeamMember.position}</p>
                <a 
                  href={selectedTeamMember.linkedinUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="linkedin-link"
                >
                  <img 
                    src="https://cdn.jsdelivr.net/npm/simple-icons@v9/icons/linkedin.svg" 
                    alt="LinkedIn"
                    className="linkedin-icon"
                  />
                </a>
              </Col>
              <Col md={8}>
                <h6>About</h6>
                <p>{selectedTeamMember.bio}</p>
                
                {/* <h6>Expertise</h6>
                <ul>
                  {selectedTeamMember.expertise.map((skill, index) => (
                    <li key={index}>{skill}</li>
                  ))}
                </ul> */}
              </Col>
            </Row>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default About;